import Spacer from '@berlitz/spacer'
import NotesBook from '@berlitz/streamline-icons/lib/regular/11-Content/03-Notes/NotesBook'
import ELearningBookLaptop from '@berlitz/streamline-icons/lib/regular/36-School-Learning/03-E-Learning/ELearningBookLaptop'
import PortalStudentProfile from '@classes/StudentProfile'
import AllLevelsCompletionModal from '@components/AllLevelsCompletionModal'
import AtRiskNotificationCard from '@components/AtRiskNotificationCard'
import Collapse from '@components/Collapse'
import EmptyLearningPath from '@components/EmptyLearningPath'
import LPNotificationCard from '@components/LPNotificationCard'
import MaterialNoLearningPath from '@components/MaterialNoLearningPath'
import { TransparentModal } from '@components/Modal'
import { StoreProps } from '@interfaces/StoreState'
import ProgramSelector from '@layouts/Dashboard/HeroBlock/ProgramSelector'
import { Provider } from '@layouts/LearningPath/Context'
import PanelCulture from '@layouts/LearningPath/Layout/PanelCulture'
import PanelPath from '@layouts/LearningPath/Layout/PanelPath'
import { FLEX, FLEX_VERSION, JPN_FLEX, NOLPID, PROFILE_TYPE } from '@utils/constants'
import { decodeDDUniqueKey, getQueryParams } from '@utils/helpers'
import React, { Fragment, useMemo } from 'react'
import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Col, Row } from 'react-styled-flexboxgrid'
import { LayoutProp } from './interface'
import {
  Content,
  IconBadge,
  LegendContainer,
  MaintenanceBannerContainer,
  PageHeaderCtr,
  SelectorContainer,
  SelectorWrapper,
  StyledVideo,
} from './style'
import useCTPermission from '@hooks/useCTPermission'
import EmptyLearningPathV2 from '@components/EmptyLearningPathV2'
import EmptyLearningPathV3 from '@components/EmptyLearningPathV3'
import OPTPanel from './OPTPanel'
import Select from '@components/Select'
import { getStoredSelectedModule, useLPContext } from './Context/LPContext'
import { activeLearningPathUpdated, registrationSelector } from '@components/ActiveState/activeStateSlice'
import PrepareNextLevelBanner from '@components/PreparingNxtLvlBanner'
import Loader from '@layouts/LearningPath/Layout/Loader'
import { learningPathSelector } from '@components/ActiveState/activeStateSlice'

const Layout: React.FC<LayoutProp> = ({
  error,
  LearningPaths,
  back = '/',
  activeDropdownRegistrationId,
  activeDropdownProgramId,
  activeDropdownLPId,
  StudentProfile,
  sfId 
}) => {
  const {
    profile,
    activeDashboardRegistration,
    activeDashboardProgram,
    activeDashboardMaterial,
    userPermissions,
    studentProfile,
    isStudent,
  } = useSelector(({ userProfile, dashboard, permissions }: StoreProps) => ({
    profile: userProfile.info,
    studentProfile: userProfile.info?.StudentProfile?.StudentProfileInfo
      ? new PortalStudentProfile(
          userProfile.info?.StudentProfile?.StudentProfileInfo,
          (activeDropdownLPId || dashboard?.activeMaterial) ?? ''
        )
      : StudentProfile || new PortalStudentProfile(userProfile.info?.StudentProfile?.StudentProfileInfo),
    activeDashboardMaterial: dashboard.activeMaterial || undefined,
    activeDashboardProgram: dashboard.activeProgram || undefined,
    activeDashboardRegistration: dashboard.activeRegistration || undefined,
    userPermissions: permissions.permissions,
    isStudent: userProfile?.info?.Type === PROFILE_TYPE.STUDENT,
    dash: dashboard,
  }))
  // check to see if the activeDropdownLPId || activeDashboardMaterial is part of the MaterialsNonISC
  // if not return the first item in the dropdown
  const firstValidMaterial = decodeDDUniqueKey(
    studentProfile.LPMaterialDropDown2?.filter((material) => !material.value.includes('undefined'))?.[0]?.value
  )
  const matchedMaterial = studentProfile.MaterialsNonISC?.[activeDropdownLPId || activeDashboardMaterial || '']
  const activeMaterial = matchedMaterial
    ? activeDropdownLPId || activeDashboardMaterial
    : firstValidMaterial.materialLPID

  const activeProgram =
    matchedMaterial && (activeDropdownProgramId || activeDashboardProgram)
      ? activeDropdownProgramId || activeDashboardProgram
      : firstValidMaterial.programId

  const activeRegistration =
    matchedMaterial && (activeDropdownRegistrationId || activeDashboardRegistration)
      ? activeDropdownRegistrationId || activeDashboardRegistration
      : firstValidMaterial.registrationId

  const { getPermissionByProgramType } = useCTPermission()

  const curriculumTestId = getQueryParams('dd')
  const AYCLFlag = userPermissions['flag::AYCL-feature']?.show
  const LPModuleFlag = userPermissions['flag::LP-modules']?.show
  const [active, setActive] = React.useState(0)
  const [activeHover, setActiveHover] = React.useState(0)
  const [playVideo, setPlayVideo] = React.useState('')
  const [validPath, setValidPath] = React.useState(
    LearningPaths?.getSelectedLearningPath(curriculumTestId || activeMaterial)
  )
  const [hasNonFlexPaths, setHasNonFlexPaths] = React.useState(LearningPaths?.hasNonFlexPaths(validPath))
  const [hasMaterialDD, setHasMaterialDD] = React.useState(true)

  const learningPath = useSelector(learningPathSelector.selectLearningPath)
  const registrationIsActive = useSelector(registrationSelector.selectIsActive)
  const dispatch = useDispatch()

  // module dropdown will always be based from the modules of the first learning path
  const moduleDD = validPath?.[0]?.Modules

  const { lpModule: selectedModule, setLPModule: setSelectedModule } = useLPContext()
  const intl = useIntl()

  const [registration, setRegistration] = React.useState(() => {
    /**
     * If there are no active material at this point yet, return undefined
     * to avoid CT to query test materials that are not related to the path
     */
    if (!activeMaterial) {
      return undefined
    }

    let reg = studentProfile?.student?.RegistrationWrappers?.find(({ RegistrationInfo }) =>
      RegistrationInfo?.Materials?.find(
        (material) => material?.LPID === curriculumTestId || material?.LPID === activeMaterial
      )
    )

    if (!reg) {
      reg = profile?.Type === PROFILE_TYPE.INSTRUCTOR ? studentProfile?.student?.RegistrationWrappers?.[0] : undefined
    }

    return reg
  })
  // used to determine if the lp that has no valid path is a flex elective that is still loading
  const regMaterial = useMemo(
    () => registration?.RegistrationInfo?.Materials?.find((material) => material.LPID === activeMaterial),
    [activeMaterial, registration]
  )

  useEffect(() => {
    if (validPath?.length) {
      localStorage.setItem('lmsLocale', (validPath?.[0]?.Locale ?? '').trim())
    }
  }, [validPath])

  useEffect(() => {
    if (!moduleDD || !LPModuleFlag) {
      return
    }
    // check which cmid are they on from the activity so we can set the correct module
    const LP = validPath?.[0]
    const lsCMIDKey = `${studentProfile?.SFId}-${LP?.Id}-current-activity`
    const currentCMID = localStorage.getItem(lsCMIDKey)

    if (currentCMID) {
      const currModule = LP?.getModuleByCMID(currentCMID)

      if (currModule) {
        setSelectedModule(currModule, studentProfile.SFId, validPath?.[0]?.Id)
        localStorage.removeItem(lsCMIDKey)
        return
      }
    }

    // set the value of the default module once valid path has been defined
    const storedModule = getStoredSelectedModule(studentProfile.SFId, validPath?.[0]?.Id)
    let tempModuleVal = moduleDD?.length ? 1 : 0
    // just making sure that the stored module value exists on the learning path, if not then we default to 1
    if (storedModule && validPath?.[0]?.Modules?.[storedModule - 1]) {
      tempModuleVal = storedModule
    }

    setSelectedModule(tempModuleVal, studentProfile.SFId, validPath?.[0]?.Id)
  }, [validPath, moduleDD])

  useEffect(() => {
    const selectedPath = LearningPaths?.getSelectedLearningPath(curriculumTestId || activeMaterial)
    setValidPath(selectedPath)

    if (selectedPath?.[0] && !learningPath) {
      dispatch(activeLearningPathUpdated(selectedPath[0]))
    }

    setHasNonFlexPaths(LearningPaths?.hasNonFlexPaths(selectedPath))
    setRegistration(
      studentProfile?.student?.RegistrationWrappers?.find(({ RegistrationInfo }) =>
        RegistrationInfo?.Materials?.find(
          (material) => material?.LPID === curriculumTestId || material?.LPID === activeMaterial
        )
      )
    )
  }, [curriculumTestId, activeMaterial])

  useEffect(() => {
    if (activeMaterial && activeProgram && activeRegistration) {
      studentProfile?.storeActiveDropdownIds(activeRegistration, activeProgram, activeMaterial)
    }
  }, [activeMaterial, activeProgram, activeRegistration])

  const PathHasNoLPID = useMemo(
    () => (curriculumTestId || activeMaterial)?.split('-')?.[0] === NOLPID,
    [curriculumTestId, activeMaterial]
  )

  /**
   * Clear any reference for CT in local storage
   */
  localStorage.setItem('CTrefreshREF', '')

  /**
   * Lets handle OPT here
   */
  if (studentProfile?.OPT?.IsRequired) {
    if (!registrationIsActive) {
      return (
        <MaintenanceBannerContainer>
          <EmptyLearningPathV3 />
        </MaintenanceBannerContainer>
      )
    }
    return <OPTPanel LearningPaths={LearningPaths} StudentProfile={StudentProfile} activeMaterial={activeMaterial} />
  }

  /**
   * Lets handle error here
   * There are 2 types of error UI and we are going
   * to show it based on what we received from the server.
   *
   * if error message contains "graphql" text then we show EmptyLearningPathV3
   * otherwise show EmptyLEarningPathV2
   */
  if (error) {
    if (error.message.toLowerCase().includes('user not found')) {
      return (
        <MaintenanceBannerContainer>
          <PrepareNextLevelBanner />
        </MaintenanceBannerContainer>
      )
    }

    if (error.message.toLowerCase().includes('graphql')) {
      return (
        <MaintenanceBannerContainer>
          <EmptyLearningPathV3 description={error.message} />
        </MaintenanceBannerContainer>
      )
    }

    return (
      <MaintenanceBannerContainer>
        <EmptyLearningPathV2 />
      </MaintenanceBannerContainer>
    )
  }

  const isPartOfMultiLevel = (reg) =>
    AYCLFlag &&
    [...FLEX, ...JPN_FLEX].indexOf(reg?.RegistrationInfo?.ProgramType) >= 0 &&
    [FLEX_VERSION.SINGLE, FLEX_VERSION.TWO, FLEX_VERSION.MULTI].indexOf(reg?.RegistrationInfo?.FlexVersion) >= 0

  const value = {
    active,
    activeHover,
    playVideo,
    setActive,
    setActiveHover,
    setPlayVideo,
  }

  let withPermission = Boolean(validPath?.[0]?.IsRecentlyCompletedAllYouCanLearn)
  if (validPath?.[0]?.IsCTUnlock) {
    withPermission = getPermissionByProgramType(validPath?.[0]?.Program?.ProgramTypeDisplay)
  }

  const hasModuleSelector =
    moduleDD?.length &&
    selectedModule && // this checking prevents display of the module selector when the values are not finalized yet as the default value is 0
    LPModuleFlag

  // we need to wait until we know which module (if there are any) needs to show up before displaying the path to avoid "flashing view" effect
  return !LPModuleFlag || !moduleDD?.length || (moduleDD?.length && selectedModule) ? (
    <>
      {validPath && withPermission && (
        <AllLevelsCompletionModal
          learningPath={validPath?.[0]!}
          studentProfile={studentProfile}
          registration={registration}
        />
      )}
      <Row>
        <Col xs={12} md={12} lg={studentProfile.AtRiskProgram ? 6 : 12}>
          <PageHeaderCtr>
            {/* <PageHeader title="My learning path" noClock noHR={true} /> */}
            {validPath?.[0] && !studentProfile.AtRiskProgram ? <LPNotificationCard path={validPath?.[0]} /> : null}
          </PageHeaderCtr>
          {/* {profile?.Type === PROFILE_TYPE.INSTRUCTOR && <BackButton back={back} />} */}
          <SelectorWrapper>
            {hasMaterialDD || (!hasMaterialDD && !hasModuleSelector) ? ( // don't remove container if module selector isn't present even if there are no materials
              <SelectorContainer xs={12} sm={12} md={5} lg={studentProfile.AtRiskProgram ? 5 : 3}>
                <ProgramSelector
                  defaultProgram={LearningPaths?.RecentLearningPath || activeMaterial}
                  StudentProfile={StudentProfile}
                  showCT={true}
                  LearningPaths={LearningPaths}
                  setShowSelector={hasMaterialDD ? setHasMaterialDD : undefined}
                />
              </SelectorContainer>
            ) : (
              <></>
            )}
            {hasModuleSelector ? (
              <SelectorContainer xs={12} sm={12} md={3} lg={2}>
                <Select
                  label={intl.formatMessage({ id: 'Modules:', defaultMessage: 'Modules:' })}
                  name="Modules"
                  value={selectedModule}
                  onChange={(module) => {
                    setSelectedModule(module, studentProfile.SFId, validPath?.[0]?.Id)
                  }}
                  options={moduleDD?.map((module) => ({
                    label: intl.formatMessage(
                      { id: 'Module {mod}', defaultMessage: 'Module {mod}' },
                      { mod: module || 0 }
                    ),
                    value: module,
                    data: module,
                  }))}
                  disableMargin
                  responsive
                />
              </SelectorContainer>
            ) : (
              <></>
            )}
          </SelectorWrapper>
          {(studentProfile.LPMaterialDropDown.length > 1 ||
            studentProfile.LPMaterialDropDown?.[0]?.suboptions?.length ||
            (!studentProfile.AtRiskProgram && studentProfile.LPMaterialDropDown.length <= 1)) && <Spacer size="md" />}
        </Col>
        {studentProfile.AtRiskProgram && (
          <Col xs={12} md={12} lg={6}>
            <AtRiskNotificationCard />
          </Col>
        )}
      </Row>
      <Spacer size="xxxs" />
      {validPath?.length ||
      (!validPath?.length &&
        isPartOfMultiLevel(registration) &&
        !validPath?.[0]?.HasNoUnits &&
        !studentProfile.HasMaterialButNoLPID) ||
      activeMaterial ? (
        <>
          {(validPath?.length ? validPath : [null])?.map((path, index) => {
            return (
              <Fragment key={`valid-path-${index}`}>
                <Provider value={value}>
                  <Content>
                    <Collapse
                      activeIds={[
                        path?.Id || registration?.RegistrationInfo?.RegistrationId,
                        `${path?.Id || registration?.RegistrationInfo?.RegistrationId}-culture`,
                        'error-boundary',
                      ]}
                    >
                      <div key={path?.Id || registration?.RegistrationInfo?.RegistrationId}>
                        {
                          <PanelPath
                            isCT={path?.Material?.IsTest}
                            Registration={registration}
                            Path={path || undefined}
                            StudentProfile={studentProfile}
                            selectedModule={LPModuleFlag ? selectedModule : undefined}
                            regMaterial={regMaterial}
                          />
                        }
                        {path && <PanelCulture Path={path} />}
                      </div>
                    </Collapse>
                  </Content>
                </Provider>
                {hasNonFlexPaths && !validPath?.[0]?.Material?.IsTest && (
                  <LegendContainer>
                    <div>
                      <IconBadge>
                        <NotesBook />
                      </IconBadge>
                      <FormattedMessage id="Printed material" />
                    </div>
                    <div>
                      <IconBadge>
                        <ELearningBookLaptop />
                      </IconBadge>
                      <FormattedMessage id="Digital material" />
                    </div>
                  </LegendContainer>
                )}
              </Fragment>
            )
          })}
        </>
      ) : validPath?.[0]?.HasNoUnits || studentProfile.HasMaterialButNoLPID || PathHasNoLPID ? (
        <MaterialNoLearningPath />
      ) : (
        <EmptyLearningPath buttonLabel="Raise a case" buttonLink="/support/contact-us/?id=technical-support" />
      )}

      {validPath?.[0]?.IsFoundation &&
        validPath?.[0].Material?.ElectiveMaterials?.length &&
        validPath.length === 1 &&
        !validPath?.[0]?.Material.IsTest && (
          <>
            <Provider value={value}>
              <Content>
                <Collapse
                  activeIds={
                    validPath?.[0]?.Material.ElectiveMaterials?.length
                      ? validPath?.[0]?.Material.ElectiveMaterials?.map((material) => material?.LPID)
                      : [registration?.RegistrationInfo?.RegistrationId]
                  }
                ></Collapse>
              </Content>
            </Provider>
            {hasNonFlexPaths && (
              <LegendContainer>
                <div>
                  <IconBadge>
                    <NotesBook />
                  </IconBadge>
                  <FormattedMessage id="Printed material" />
                </div>
                <div>
                  <IconBadge>
                    <ELearningBookLaptop />
                  </IconBadge>
                  <FormattedMessage id="Digital material" />
                </div>
              </LegendContainer>
            )}
          </>
        )}

      <TransparentModal
        open={Boolean(playVideo)}
        onClose={() => {
          setPlayVideo('')
        }}
      >
        <StyledVideo width="620" height="540" controls>
          <source src={playVideo} />
        </StyledVideo>
      </TransparentModal>
      <Spacer size="xxxl" />
    </>
  ) : (
    <Loader />
  )
}

export default Layout
